import { useServicesContext } from "@/modules/core/services/services.context"
import { TemplateCategoryEntity } from "@/modules/template/entities/templateCategoryEntity"
import type { EditTemplateCategoryInputDto } from "@/modules/template/gateway/templateCategoryDto"
import { templateCategoryKeys } from "@/modules/template/gateway/templateCategoryKeys"
import { DEFAULT_PAGE_SIZE } from "@/modules/utils/paginated"
import { useMutation, useQueryClient, useSuspenseInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"

// Not use for instant
export function useTemplateCategories({
  defaultPage,
  defaultPageSize,
  searchTerm,
}: {
  defaultPage?: number
  defaultPageSize?: number
  searchTerm?: string
}) {
  const { templateCategoryApi } = useServicesContext()
  const [page, setPage] = useState(defaultPage ?? 0)
  const [pageSize, setPageSize] = useState(defaultPageSize ?? DEFAULT_PAGE_SIZE)

  const { data, isLoading, error } = useSuspenseQuery({
    queryKey: templateCategoryKeys.list({ page, pageSize, searchTerm }),
    queryFn: async () => {
      const result = await templateCategoryApi.getTemplateCategories(page, pageSize, searchTerm)
      return { ...result, content: result.content.map((item) => TemplateCategoryEntity.fromDto(item)) }
    },
  })

  const handleSetPage = (newPageNumber: number) => {
    setPage(newPageNumber)
  }

  const handlePreviousPage = () => {
    if (data.page > 0) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (data.page < data.totalPages - 1) {
      setPage((prev) => prev + 1)
    }
  }

  return {
    data: data.content,
    isLoading,
    error,
    pageSize,
    setPageSize,
    handleSetPage,
    handlePreviousPage,
    handleNextPage,
  }
}
export function useTemplateCategoriesInfinite({
  searchTerm,
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE,
}: {
  searchTerm?: string
  page?: number
  pageSize?: number
}) {
  const { templateCategoryApi } = useServicesContext()

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery({
    queryKey: [...templateCategoryKeys.list({ page, pageSize, searchTerm })],
    queryFn: async ({ pageParam }) => {
      const result = await templateCategoryApi.getTemplateCategories(pageParam, pageSize, searchTerm)
      return { ...result, content: result.content.map((item) => TemplateCategoryEntity.fromDto(item)) }
    },
    initialPageParam: page,
    getNextPageParam: (lastPage) => {
      const hasMorePages = lastPage.page < lastPage.totalPages - 1
      if (!hasMorePages) {
        return null
      }

      return lastPage.page + 1
    },
  })

  const categories = useMemo(() => data.pages.flatMap((dataPage) => dataPage.content), [data])

  return {
    data: categories,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}

export function useCreateTemplateCategory() {
  const { templateCategoryApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (editTemplateCategoryInput: EditTemplateCategoryInputDto) => {
      await templateCategoryApi.createTemplateCategory(editTemplateCategoryInput)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateCategoryKeys.list() })
    },
  })
}

export function useUpdateTemplateCategory() {
  const { templateCategoryApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      editTemplateCategoryInput,
    }: {
      id: string
      editTemplateCategoryInput: EditTemplateCategoryInputDto
    }) => {
      await templateCategoryApi.updateTemplateCategory(id, editTemplateCategoryInput)
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateCategoryKeys.list() })
    },
  })
}

export function useDeleteTemplateCategory() {
  const { templateCategoryApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await templateCategoryApi.deleteTemplateCategory(id)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateCategoryKeys.list() })
    },
  })
}
